import { formatClockTime } from '@/shared/lib/time-format';
import { useEffect, useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useRescheduleAppointment } from '@/scheduling/services/use-reschedule-appointment';
import type { AppointmentsDetailsResponse } from '@/types/scheduling-api';
import { APPOINTMENT_LIST_QUERIES } from '@/scheduling/services/appointment-list-keys';

/**
 * RescheduleAppointmentDialog — date + start-time picker that PUTs to
 * /api/Appointment/Reschedule.
 *
 * Flutter routes Reschedule to the full ViewAppointment screen
 * (patient_appointments_tab.dart:1694) where the user adjusts date/time
 * inline. We don't have that screen yet, so this is a focused dialog
 * that does the same job: pick date, pick start time, keep duration.
 *
 * On confirm, the cached AppointmentsDetailsResponse is updated optimistically
 * (start/end fields swapped); SignalR's UpdateAppointments push reconciles.
 */

interface RescheduleTarget {
  appointmentId: string;
  /** Original ISO start. Drives the duration calculation + the picker
   *  initial state. */
  startISO: string;
  endISO: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: RescheduleTarget | null;
  /**
   * Optional submit interceptor. When provided, the dialog calls this
   * with the new (`startISO`, `endISO`) values INSTEAD OF its internal
   * `useRescheduleAppointment` mutation + cache patching. The caller
   * owns the mutation, the cache invalidation, AND the toast.
   *
   * Use case (PROD-852): for recurring appointments with `following` /
   * `all` scope, the caller routes to `useSeriesReschedule` which hits
   * `PUT /api/Appointment/{id}` with an `editRecurring` payload — a
   * different endpoint than the focused `/api/Appointment/Reschedule`
   * the dialog uses by default. The override keeps the dialog UI
   * unchanged while letting the caller pick the wire path.
   *
   * Return value semantics (Samer PR #281 review §2):
   *   - `true` (or `undefined` — back-compat for void returns) →
   *     dialog closes
   *   - `false` → dialog stays open so the user can retry. Caller is
   *     expected to have surfaced an error toast already.
   *
   * Return a promise so the dialog can await it before closing.
   * Synchronous returns work too.
   */
  onSubmitOverride?: (
    newStartISO: string,
    newEndISO: string,
  ) => Promise<boolean | void> | boolean | void;
}

export function RescheduleAppointmentDialog({
  open,
  onOpenChange,
  target,
  onSubmitOverride,
}: Props) {
  const queryClient = useQueryClient();
  const reschedule = useRescheduleAppointment();

  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState<string>(''); // "HH:mm"
  const [overrideBusy, setOverrideBusy] = useState(false);

  // Reset on open with the appointment's existing start as the seed.
  useEffect(() => {
    if (!open || !target) return;
    const start = new Date(target.startISO);
    setDate(start);
    setTime(toHHMM(start));
    setOverrideBusy(false);
  }, [open, target]);

  if (!target) return null;

  const durationMs =
    new Date(target.endISO).getTime() - new Date(target.startISO).getTime();

  const isPending = onSubmitOverride ? overrideBusy : reschedule.isPending;
  const canSubmit = Boolean(date && time && !isPending);

  const submit = async () => {
    if (!date || !time) return;
    const [hh, mm] = time.split(':').map((n) => parseInt(n, 10));
    if (Number.isNaN(hh) || Number.isNaN(mm)) return;

    const newStart = new Date(date);
    newStart.setHours(hh, mm, 0, 0);
    const newEnd = new Date(newStart.getTime() + durationMs);

    const startISO = newStart.toISOString();
    const endISO = newEnd.toISOString();

    // Override path: caller owns mutation, cache, and toast. The dialog
    // only handles the date/time UI + close-on-success.
    //
    // The `return` below is load-bearing — it suppresses the
    // `reschedule.mutateAsync(...)` call further down (Samer PR #281
    // review §1). Without it, BOTH paths would fire and a recurring
    // series would receive a ghost single-occurrence reschedule
    // alongside the series update. `useRescheduleAppointment()` is
    // still called at the top of the component (Rules of Hooks
    // require it), but the hook is a useMutation factory — it doesn't
    // touch the network until `mutateAsync` runs, which the early
    // return prevents.
    //
    // Dialog stays open on `false` so the user can retry without
    // re-picking the scope (Samer review §2). Caller is expected to
    // have surfaced an error toast already.
    if (onSubmitOverride) {
      setOverrideBusy(true);
      try {
        const result = await onSubmitOverride(startISO, endISO);
        if (result !== false) {
          onOpenChange(false);
        }
      } finally {
        setOverrideBusy(false);
      }
      return;
    }

    await reschedule.mutateAsync({
      appointmentId: target.appointmentId,
      startDate: startISO,
      endDate: endISO,
    });

    // Optimistic: swap start/end in every cached appointments query.
    queryClient.setQueriesData<AppointmentsDetailsResponse | undefined>(
      APPOINTMENT_LIST_QUERIES,
      (prev) => {
        if (!prev?.appointments) return prev;
        const idx = prev.appointments.findIndex(
          (a) => a.id === target.appointmentId,
        );
        if (idx < 0) return prev;
        const next = prev.appointments.slice();
        next[idx] = { ...next[idx], start: startISO, end: endISO };
        return { ...prev, appointments: next };
      },
    );
    void queryClient.invalidateQueries(APPOINTMENT_LIST_QUERIES);

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[var(--dialog-width-form)]">
        <DialogHeader>
          <DialogTitle>Reschedule appointment</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <div className="[font:var(--type-body)] text-[var(--ink-meta)]">
            Currently {formatRange(target.startISO, target.endISO)} ({Math.round(
              durationMs / 60000,
            )} min)
          </div>

          <div>
            <label className="text-sm font-semibold text-text-primary block mb-2">
              New date
            </label>
            <div className="border border-[var(--surface-line)] rounded-card p-2">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                showOutsideDays
                fixedWeeks
                disabled={{ before: stripTime(new Date()) }}
              />
              {/* Footer matches the Signal-DS DatePicker: blue "Today" link + value. */}
              <div className="mt-2 flex items-center justify-between border-t border-[var(--surface-line)] pt-2 text-size-sm">
                <button
                  type="button"
                  onClick={() => setDate(stripTime(new Date()))}
                  className="font-medium text-brand-primary hover:underline"
                >
                  Today
                </button>
                <span className="text-text-muted">
                  {date
                    ? date.toLocaleDateString(undefined, {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                      })
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="reschedule-time"
              className="text-sm font-semibold text-text-primary block mb-2"
            >
              New start time
            </label>
            <input
              id="reschedule-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full h-10 px-3 text-sm rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
            <p className="text-xs text-text-hint mt-1">
              Duration stays at {Math.round(durationMs / 60000)} min.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {isPending ? 'Rescheduling…' : 'Reschedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

function toHHMM(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`;
}

function stripTime(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function formatRange(startISO: string, endISO: string): string {
  const fmt = (iso: string) => formatClockTime(new Date(iso));
  const date = new Date(startISO).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return `${date} · ${fmt(startISO)} – ${fmt(endISO)}`;
}
